import { Event } from "../../types/events";
import { Resource } from "../../types/resources";

/**
 * Lane assignment for a single resource row. `assignments` maps a stringified
 * eventId to the zero-based lane (vertical "swimlane") it should occupy so
 * concurrent events do not visually overlap.
 */
export interface ResourceLaneAssignment {
  laneCount: number;
  assignments: Map<string, number>;
}

/**
 * Greedy interval-scheduling lane packer. Events are sorted by start time and
 * each one is placed into the first lane whose previous event has already
 * ended; otherwise a new lane is created. The caller decides how to render
 * each lane (height, top offset, etc).
 *
 * Pure function — exported for unit testing and reuse from the row builder
 * and the grid renderer.
 */
export const calculateLaneAssignments = (
  events: Event[]
): ResourceLaneAssignment => {
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.timeRange.start).getTime() -
      new Date(b.timeRange.start).getTime()
  );

  const laneEndTimes: number[] = [];
  const assignments = new Map<string, number>();

  sorted.forEach((eventItem) => {
    const start = new Date(eventItem.timeRange.start).getTime();
    const end = new Date(eventItem.timeRange.end).getTime();

    let assignedLane = -1;
    for (let index = 0; index < laneEndTimes.length; index += 1) {
      if (start >= laneEndTimes[index]) {
        assignedLane = index;
        break;
      }
    }

    if (assignedLane === -1) {
      assignedLane = laneEndTimes.length;
      laneEndTimes.push(end);
    } else {
      laneEndTimes[assignedLane] = end;
    }

    assignments.set(String(eventItem.id), assignedLane);
  });

  return {
    laneCount: Math.max(1, laneEndTimes.length),
    assignments,
  };
};

/**
 * Builds a `resourceId -> ResourceLaneAssignment` map for every resource in
 * the visible timeline. Resources without any events still appear in the map
 * with `laneCount: 1` so callers can size rows uniformly.
 */
export const buildResourceLaneMap = (
  resources: Resource[],
  events: Event[],
  getEventResourceIds: (event: Event) => (string | number)[]
): Map<string, ResourceLaneAssignment> => {
  const map = new Map<string, ResourceLaneAssignment>();

  resources.forEach((resource) => {
    const resourceIdStr = String(resource.id);
    const resourceEvents = events.filter((eventItem) => {
      const ids = getEventResourceIds(eventItem) || [];
      return ids.some((id) => String(id) === resourceIdStr);
    });

    map.set(resourceIdStr, calculateLaneAssignments(resourceEvents));
  });

  return map;
};
